import { Button } from '@oysterjs/ui/button';
import { Description, Field, Label } from '@oysterjs/ui/fieldset';
import { Select } from '@oysterjs/ui/select';

import { type MailboxAccountFieldsFragment, MailboxAccountStatus } from '~/api/graphql';
import { InlineNotice } from '~/components/inline-notice';
import { MAILBOX_SETTINGS_PATH } from '~/components/mailbox/bulk-send-paths';
import { mailClientName } from '~/lib/mailbox-providers';
import type { MailboxBulkSendUsage } from './bulk-draft-actions';

export const NEEDS_RECONNECT_REASON = 'Reconnect your mailbox in Settings before sending.';

const isConnected = (account: MailboxAccountFieldsFragment) =>
  account.status === MailboxAccountStatus.Connected;

/** The default mailbox when it can send, otherwise the first one that can. */
export const defaultSendingMailboxId = (
  mailboxAccounts: MailboxAccountFieldsFragment[]
): string | undefined => {
  const connected = mailboxAccounts.filter(isConnected);
  return (connected.find((account) => account.isDefault) ?? connected[0])?.id;
};

/**
 * Where the bulk emails go out from, and so where replies come back to, with the chosen
 * mailbox's daily bulk limit.
 */
export const BulkDraftSendFrom = ({
  mailboxAccounts,
  mailboxAccountId,
  usage,
  onChange
}: {
  mailboxAccounts: MailboxAccountFieldsFragment[];
  mailboxAccountId: string | undefined;
  /** The chosen mailbox's; undefined while it loads or when its read failed. */
  usage: Pick<MailboxBulkSendUsage, 'dailyLimit'> | undefined;
  onChange: (mailboxAccountId: string) => void;
}) => (
  <section className="mb-4 rounded-lg bg-white p-4 shadow sm:p-6 dark:bg-neutral-800">
    {mailboxAccounts.length === 0 ? (
      <div className="text-sm">
        <span className="block font-medium text-neutral-900 dark:text-white">
          Connect a mailbox before you send in bulk.
        </span>
        <span className="mt-1 block text-neutral-500 dark:text-neutral-400">
          Bulk emails go out from your own mailbox, so replies come back to you.
        </span>
        <Button color="white" className="mt-3" href={MAILBOX_SETTINGS_PATH}>
          Go to Settings &gt; Mailboxes
        </Button>
      </div>
    ) : (
      <Field className="sm:max-w-md">
        <Label>Send from</Label>
        <Select value={mailboxAccountId ?? ''} onChange={(event) => onChange(event.target.value)}>
          {!mailboxAccountId && (
            <option value="" disabled>
              Choose a mailbox
            </option>
          )}
          {mailboxAccounts.map((account) => (
            <option
              key={account.id}
              value={account.id}
              disabled={!isConnected(account)}
              title={isConnected(account) ? undefined : NEEDS_RECONNECT_REASON}
            >
              {account.identity.email}
              {isConnected(account) ? '' : ' (Needs reconnect)'}
            </option>
          ))}
        </Select>
        <Description>Replies come back to this mailbox.</Description>
        {mailboxAccountId && usage && (
          <Description>
            Bulk limit: {usage.dailyLimit.toLocaleString()} emails per 24 hours.
          </Description>
        )}
      </Field>
    )}
  </section>
);

/** Shown when every mailbox the agent could send from has to be reconnected first. */
export const BulkDraftReconnectNotice = ({
  account
}: {
  account: MailboxAccountFieldsFragment;
}) => {
  const mailClient = mailClientName(account.provider);
  return (
    <InlineNotice
      tone="warning"
      className="mb-4"
      title={`${account.identity.email} needs to be reconnected.`}
      description={`${mailClient} signed Cara out of this mailbox. Reconnect it, then come back here. Nothing has been sent.`}
      actions={
        <Button color="sky" href={MAILBOX_SETTINGS_PATH}>
          Reconnect {mailClient}
        </Button>
      }
    />
  );
};
